import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DinnerDiningIcon from "@mui/icons-material/DinnerDining";
import SearchIcon from "@mui/icons-material/Search";
import { useLocation, useNavigate } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import { getRecipes } from "../api/recipesApi";
import RecetasTable from "../components/RecetasTable";
import {
  formatRecipeSectors,
  RECIPE_SECTORS,
} from "../utils/recipeSector.utils";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 700;

const selectSx = {
  width: { xs: "100%", md: 126 },
  height: 38,
  borderRadius: "33px",
  bgcolor: "neutral.100",
  color: "primary.900",
  fontSize: 13,
  fontWeight: 700,
  boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "secondary.500",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    py: 0,
  },
};

function FilterSelect({ value, onValueChange, placeholder, getLabel, children }) {
  return (
    <Select
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      displayEmpty
      size="small"
      IconComponent={value ? () => null : undefined}
      sx={{
        ...selectSx,
        "& .MuiSelect-select": {
          ...selectSx["& .MuiSelect-select"],
          pr: value ? "32px !important" : undefined,
        },
      }}
      renderValue={(selectedValue) =>
        selectedValue ? getLabel?.(selectedValue) || selectedValue : placeholder
      }
      endAdornment={
        value ? (
          <InputAdornment
            position="end"
            sx={{ position: "absolute", right: 8, color: "primary.900" }}
          >
            <IconButton
              size="small"
              aria-label={`Limpiar filtro ${placeholder.toLowerCase()}`}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
                onValueChange("");
              }}
              sx={{
                width: 24,
                height: 24,
                opacity: 0.7,
                "&:hover": { opacity: 1 },
              }}
            >
              <ClearIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </InputAdornment>
        ) : null
      }
    >
      {children}
    </Select>
  );
}

function errorToMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message ||
    "Error al cargar recetas."
  );
}

function normalizeRecipe(recipe) {
  if (!recipe) return null;

  return {
    ...recipe,
    name: recipe.name || "-",
    sectorLabel: formatRecipeSectors(recipe.sector),
    isActive: Boolean(recipe.isActive),
  };
}

function CardShell({ children, sx }) {
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "neutral.300",
        borderRadius: "30px",
        boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default function RecetasPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const latestRequestId = useRef(0);

  const [recipes, setRecipes] = useState([]);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [nameFilterInput, setNameFilterInput] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [successMessage, setSuccessMessage] = useState(
    location.state?.successMessage || ""
  );

  useEffect(() => {
    if (!location.state?.successMessage) return;
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state?.successMessage, navigate]);

  const fetchRecipes = useCallback(async (signal) => {
    const requestId = ++latestRequestId.current;
    setLoading(true);
    setError("");

    try {
      const data = await getRecipes({
        page,
        limit: PAGE_SIZE,
        name: nameFilter || undefined,
        sector: sectorFilter || undefined,
        isActive: statusFilter || undefined,
        signal,
      });
      if (signal.aborted || requestId !== latestRequestId.current) return;

      const list = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];
      const normalized = list
        .map(normalizeRecipe)
        .filter((item) => item?.id != null);
      const responseTotalItems = data?.meta?.totalItems ?? normalized.length;
      const responseTotalPages =
        data?.meta?.totalPages ?? (normalized.length ? 1 : 0);
      const lastValidPage = Math.max(responseTotalPages, 1);

      if (page > lastValidPage) {
        setRecipes([]);
        setTotalItems(responseTotalItems);
        setTotalPages(responseTotalPages);
        setPage(lastValidPage);
        return;
      }

      setRecipes(normalized);
      setTotalItems(responseTotalItems);
      setTotalPages(responseTotalPages);
    } catch (requestError) {
      if (signal.aborted || requestId !== latestRequestId.current) return;
      setRecipes([]);
      setTotalItems(0);
      setTotalPages(0);
      setError(errorToMessage(requestError));
    } finally {
      if (!signal.aborted && requestId === latestRequestId.current) {
        setLoading(false);
      }
    }
  }, [nameFilter, page, sectorFilter, statusFilter]);

  const commitNameFilter = useCallback(
    (value) => {
      const normalized = String(value ?? "").trim();
      if (nameFilter === normalized) return;

      setNameFilter(normalized);
      setPage(1);
    },
    [nameFilter]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchRecipes(controller.signal);

    return () => controller.abort();
  }, [fetchRecipes]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      commitNameFilter(nameFilterInput);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [commitNameFilter, nameFilterInput]);

  const pageSummary = useMemo(() => {
    if (!totalItems) return "Sin recetas para mostrar";
    return `Mostrando ${recipes.length} de ${totalItems} recetas`;
  }, [recipes.length, totalItems]);

  function handleClearNameFilter(event) {
    event.stopPropagation();
    setNameFilterInput("");
    setNameFilter("");
    setPage(1);
  }

  function handleNameFilterKeyDown(event) {
    if (event.key !== "Enter") return;
    event.preventDefault();
    event.stopPropagation();
    commitNameFilter(nameFilterInput);
  }

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        overflow: { xs: "auto", md: "hidden" },
      }}
    >
      <StockPageHeader
        title="Biblioteca de recetas"
        onBack={() => navigate("/stock/base-de-datos/productos-de-venta")}
      />

      <CardShell sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ alignItems: { xs: "stretch", sm: "center" } }}
            >
              <FilterSelect
                value={sectorFilter}
                onValueChange={(value) => {
                  setSectorFilter(value);
                  setPage(1);
                }}
                placeholder="Sector"
                getLabel={(value) => value}
              >
                <MenuItem value="">Todos los sectores</MenuItem>
                {RECIPE_SECTORS.map((sector) => (
                  <MenuItem key={sector} value={sector}>
                    {sector}
                  </MenuItem>
                ))}
              </FilterSelect>

              <FilterSelect
                value={statusFilter}
                onValueChange={(value) => {
                  setStatusFilter(value);
                  setPage(1);
                }}
                placeholder="Estado"
                getLabel={(value) => (value === "true" ? "Activo" : "Inactivo")}
              >
                <MenuItem value="">Todos los estados</MenuItem>
                <MenuItem value="true">Activo</MenuItem>
                <MenuItem value="false">Inactivo</MenuItem>
              </FilterSelect>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ alignItems: { xs: "stretch", sm: "center" } }}
            >
              <TextField
                value={nameFilterInput}
                onChange={(event) => setNameFilterInput(event.target.value)}
                onBlur={(event) => commitNameFilter(event.target.value)}
                onKeyDown={handleNameFilterKeyDown}
                placeholder="Buscar"
                size="small"
                slotProps={{ htmlInput: { maxLength: 200 } }}
                sx={{
                  width: { xs: "100%", md: 200 },
                  "& .MuiOutlinedInput-root": {
                    height: 38,
                    borderRadius: "33px",
                    bgcolor: "neutral.50",
                    color: "primary.900",
                    fontSize: 13,
                    fontWeight: 600,
                    boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "neutral.700",
                    opacity: 0.9,
                    fontWeight: 600,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "primary.900", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: nameFilterInput ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Limpiar búsqueda"
                        onClick={handleClearNameFilter}
                        sx={{
                          color: "primary.900",
                          width: 24,
                          height: 24,
                          opacity: 0.7,
                          "&:hover": { opacity: 1 },
                        }}
                      >
                        <ClearIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              <Button
                type="button"
                variant="outlined"
                startIcon={<DinnerDiningIcon sx={{ fontSize: 18 }} />}
                onClick={() => navigate("/stock/base-de-datos/productos-de-venta")}
                sx={{
                  minWidth: { xs: "100%", sm: 154 },
                  height: 38,
                  borderRadius: "33px",
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                Productos de venta
              </Button>

              <Button
                type="button"
                onClick={() => navigate("/stock/base-de-datos/recetas/crear")}
                sx={{
                  minWidth: { xs: "100%", sm: 154 },
                  height: 38,
                  px: 2.4,
                  borderRadius: "33px",
                  bgcolor: "primary.500",
                  color: "logoblanco",
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 800,
                  boxShadow: "0px 4px 12px rgba(23, 31, 76, 0.18)",
                  "&:hover": { bgcolor: "primary.600" },
                }}
              >
                Agregar recetas
              </Button>
            </Stack>
          </Stack>

          {!!error && (
            <Box
              sx={{
                px: 2,
                py: 1.2,
                borderRadius: "18px",
                bgcolor: "secondary.700",
                boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.35)",
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "logoblanco" }}>
                No se pudo completar la operación
              </Typography>
              <Typography
                sx={{ fontSize: 12, fontWeight: 600, color: "logoblanco", opacity: 0.95 }}
              >
                {error}
              </Typography>
            </Box>
          )}

          <RecetasTable
            rows={recipes}
            loading={loading}
            emptyMessage={
              nameFilter || sectorFilter || statusFilter
                ? "No se encontraron recetas con los filtros seleccionados."
                : "Todavía no hay recetas cargadas."
            }
            onViewRecipe={(recipe) =>
              navigate(`/stock/base-de-datos/recetas/${recipe.id}/receta`)
            }
            onEdit={(recipe) =>
              navigate(`/stock/base-de-datos/recetas/${recipe.id}/editar`)
            }
          />

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            sx={{
              flexShrink: 0,
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              pt: 0.5,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900" }}>
              {pageSummary}
            </Typography>

            <Pagination
              page={page}
              count={Math.max(totalPages, 1)}
              onChange={(_, value) => setPage(value)}
              shape="rounded"
              color="primary"
              disabled={loading || totalPages <= 1}
            />
          </Stack>
        </Stack>
      </CardShell>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4500}
        onClose={(_, reason) => {
          if (reason !== "clickaway") setSuccessMessage("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessMessage("")}
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
